import React from 'react';
import PropTypes from 'prop-types';

// One frame for every full-height admin view, so that content, toolbars and panels line up the
// same way on all of them.
//
// Before this there were two shapes in play: centered-container pages (the AI prompt editors)
// and full-height panel pages (the form editor, the tag tree). A page that mixed them — tabs
// where one tab was a container and another was full-bleed — made the tab bar visibly jump
// between tabs, which is what prompted pulling this out.
//
// The numbers are not arbitrary:
//   TOPBAR_HEIGHT is TopBar's rendered height, so a view fills exactly the space below it.
//   GUTTER matches TopBar's own px-4, so content aligns with the page title above it.
//   PANEL_WIDTH / PANEL_GUTTER are the side-panel values already shared by FormEditPage and
//   TagsPage; panels are narrow, so they get the tighter gutter.
export const TOPBAR_HEIGHT = 54;
export const GUTTER = 'px-4';
export const PANEL_WIDTH = 392;
export const PANEL_GUTTER = 'px-3';

// Width at which long-form text stops being comfortable to read. Content is capped but stays
// left-aligned rather than centered: centering is what makes a toolbar appear to move when the
// tab under it changes.
export const READABLE_MAX_WIDTH = 960;

export default function WorkspaceView({ toolbar, children }) {
  return (
    <div
      className="d-flex flex-column"
      style={{ height: `calc(100vh - ${TOPBAR_HEIGHT}px)`, minHeight: 0, overflow: 'hidden' }}
    >
      {toolbar && (
        <div className={`${GUTTER} pt-3 flex-shrink-0 bg-white border-bottom`}>{toolbar}</div>
      )}
      <div className="flex-grow-1 d-flex" style={{ minHeight: 0, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

WorkspaceView.propTypes = {
  toolbar: PropTypes.node,
  children: PropTypes.node,
};

WorkspaceView.defaultProps = { toolbar: null, children: null };

// A padded, scrolling content region — the body for anything that is a document rather than a
// set of panels (prompt editors, status tables).
export function WorkspaceScroll({ children, readable, className }) {
  return (
    <div className={`flex-grow-1 overflow-auto ${GUTTER} py-4 ${className}`} style={{ minHeight: 0, minWidth: 0 }}>
      <div style={readable ? { maxWidth: READABLE_MAX_WIDTH } : undefined}>{children}</div>
    </div>
  );
}

WorkspaceScroll.propTypes = {
  children: PropTypes.node,
  readable: PropTypes.bool,
  className: PropTypes.string,
};

WorkspaceScroll.defaultProps = { children: null, readable: false, className: '' };

// The frame for a page whose body is one wide table — a report, a long list — rather than a
// document or a set of panels.
//
// Unlike WorkspaceScroll this body does not scroll: the table inside it owns both scrollbars
// (DataTable's `fill`), so the filter bar above the rows and the header row itself stay put
// while the rows move, and the horizontal scrollbar sits at the bottom of the window instead of
// at the bottom of a thousand rows.
//
// Also unlike WorkspaceScroll it adds no padding of its own. A table pane is not a card on a
// page, it *is* the page: it runs to the left, right and bottom edges, and the controls above it
// carry the GUTTER instead. DataTable's `fill` does both halves of that.
//
// Never put a Bootstrap `container` in here. A centered container re-centres as its content
// width changes, which is what made the toolbar above it appear to move — see
// docs/dev/admin-layout.md.
export function WorkspaceDataView({ toolbar, children, className }) {
  return (
    <WorkspaceView toolbar={toolbar}>
      <div
        className={`flex-grow-1 d-flex flex-column ${className}`}
        style={{ minHeight: 0, minWidth: 0 }}
      >
        {children}
      </div>
    </WorkspaceView>
  );
}

WorkspaceDataView.propTypes = {
  toolbar: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};

WorkspaceDataView.defaultProps = { toolbar: null, children: null, className: '' };

// A column with its own scroll region. The header/body split (header over a padded scrolling
// body) is the one already used by the form editor and tag tree side panels.
//
// `header` takes over the header slot for panels whose heading is more than a title — the tag
// panel carries a subtitle and a close button. Keeping that in the primitive means those panels
// still inherit the shared gutter and border instead of re-implementing the strip.
export function WorkspacePanel({ title, header, width, grow, side, actions, children, className }) {
  const edge = grow ? '' : `flex-shrink-0 border-${side === 'right' ? 'start' : 'end'}`;
  return (
    <div
      className={`bg-white d-flex flex-column ${grow ? 'flex-grow-1' : edge} ${className}`}
      style={{ width, minWidth: 0, minHeight: 0, overflow: 'hidden' }}
    >
      {(header || title) && (
        <div className={`${PANEL_GUTTER} pt-3 pb-2 flex-shrink-0 border-bottom bg-white`}>
          {header || (
            <div className="d-flex justify-content-between align-items-center gap-2">
              <h6 className="fw-semibold mb-0 text-truncate">{title}</h6>
              {actions}
            </div>
          )}
        </div>
      )}
      <div className={`flex-grow-1 overflow-auto ${PANEL_GUTTER} py-3`} style={{ minHeight: 0 }}>
        {children}
      </div>
    </div>
  );
}

WorkspacePanel.propTypes = {
  title: PropTypes.string,
  header: PropTypes.node,
  width: PropTypes.number,
  grow: PropTypes.bool,
  side: PropTypes.oneOf(['left', 'right']),
  actions: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};

WorkspacePanel.defaultProps = {
  title: null, header: null, width: undefined, grow: false, side: 'left',
  actions: null, children: null, className: '',
};
